<template>
  <div>
    <b-alert
      v-if="apiError"
      show
      variant="danger"
      class="p-1"
    >
      {{ apiError }}
    </b-alert>

    <b-tabs
      justified
      lazy
      pills
      class="mb-1"
    >
      <b-tab :title="$t('partner.order-request.tabs.basic-info')">

        <b-card class="mb-1">
          <b-row class="align-items-center">
            <b-col
              cols="12"
              md="8"
              class="mb-1 mb-md-0"
            >
              <h4 class="mb-0">
                {{ $t('partner.order-request.details.number', { id: request.id || '-' }) }}
              </h4>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="text-md-right"
            >
              <b-badge
                pill
                :variant="statusVariant(request.status)"
              >
                {{ statusLabel(request.status) }}
              </b-badge>
            </b-col>
          </b-row>

          <div class="text-muted mt-1">
            {{ $t('partner.order-request.details.updated-at') }}: {{ formatDate(request.updatedAt) }}
          </div>
        </b-card>

        <b-alert
          v-if="restoredDraftAt"
          show
          variant="info"
          class="p-1"
        >
          {{ $t('partner.order-request.autosave.restored', { date: formatDate(restoredDraftAt) }) }}
        </b-alert>

        <b-alert
          v-if="decisionReason"
          show
          variant="warning"
          class="p-1"
        >
          {{ decisionReason }}
        </b-alert>

        <partner-order-request-form
          v-model="form"
          :allowed-customers="allowedCustomers"
          :allowed-order-types="allowedOrderTypes"
          :order-request-instruction="orderRequestInstruction"
          :readonly="!isEditable"
          :loading="loading || saving || submitting || cancelling"
        />

        <div class="d-flex flex-wrap gap-1 mt-1 mb-1">
          <b-button
            v-if="isEditable"
            variant="primary"
            :disabled="loading || saving || submitting || cancelling"
            @click="saveDraft"
          >
            {{ $t('partner.order-request.actions.save-draft') }}
          </b-button>

          <b-button
            v-if="canSubmit"
            variant="success"
            :disabled="loading || saving || submitting || cancelling"
            @click="submitRequest"
          >
            {{ $t('partner.order-request.actions.submit') }}
          </b-button>

          <b-button
            v-if="canCancel"
            variant="danger"
            :disabled="loading || saving || submitting || cancelling"
            @click="cancelRequest"
          >
            {{ $t('partner.order-request.actions.cancel') }}
          </b-button>

          <b-button
            v-if="isEditable"
            variant="outline-secondary"
            :disabled="loading || saving || submitting || cancelling"
            @click="clearAutosave"
          >
            {{ $t('partner.order-request.actions.clear-draft') }}
          </b-button>
        </div>
      </b-tab>

      <b-tab :title="$t('partner.order-request.tabs.status-history')">
        <b-card no-body>
          <b-table
            :items="statusHistory"
            :fields="historyFields"
            responsive
            show-empty
            :empty-text="$t('shared.no-data')"
            class="rounded-top"
          >
            <template #cell(oldStatus)="data">
              {{ data.item.oldStatus ? statusLabel(data.item.oldStatus) : '-' }}
            </template>
            <template #cell(newStatus)="data">
              {{ statusLabel(data.item.newStatus) }}
            </template>
            <template #cell(changedByUser)="data">
              {{ changedByUserLabel(data.item) }}
            </template>
            <template #cell(createdAt)="data">
              {{ formatDate(data.item.createdAt) }}
            </template>
          </b-table>
        </b-card>
      </b-tab>
    </b-tabs>

    <div class="d-flex justify-content-end mt-1">
      <b-button
        variant="flat-secondary"
        :to="{ name: 'partner-order-request-list' }"
      >
        {{ $t('partner.order-request.actions.back-to-list') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BRow,
  BTab,
  BTabs,
  BTable,
} from 'bootstrap-vue'
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import PusherClient from '@/libs/pusher'
import PartnerOrderRequestForm from './components/PartnerOrderRequestForm.vue'
import {
  buildOrderRequestPayloadFromForm,
  createEmptyOrderRequestForm,
  mapOrderRequestToForm,
  validateOrderRequestPayload,
} from './helpers/formModel'
import {
  clearOrderRequestDraft,
  getEditDraftStorageKey,
  readOrderRequestDraft,
  saveOrderRequestDraft,
} from './helpers/draftStorage'

export default {
  name: 'PartnerOrderRequestDetails',
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BRow,
    BTab,
    BTabs,
    BTable,
    PartnerOrderRequestForm,
  },
  data() {
    return {
      loading: false,
      saving: false,
      submitting: false,
      cancelling: false,
      form: createEmptyOrderRequestForm(),
      partnerOrderRequestInstruction: null,
      apiError: null,
      restoredDraftAt: null,
      refreshTimeoutId: null,
      pusherExternalOrderRequestHandler: null,
    }
  },
  computed: {
    request() {
      return this.$store.getters['externalOrderRequest/getPartnerOrderRequest']
    },
    allowedCustomers() {
      return this.$store.getters['externalOrderRequest/getAllowedCustomers']
    },
    allowedOrderTypes() {
      return this.$store.getters['externalOrderRequest/getAllowedOrderTypes']
    },
    orderRequestInstruction() {
      return this.partnerOrderRequestInstruction
    },
    isEditable() {
      return ['draft', 'changes_requested'].includes(this.request.status)
    },
    canSubmit() {
      return ['draft', 'changes_requested'].includes(this.request.status)
    },
    canCancel() {
      return ['draft', 'submitted', 'changes_requested'].includes(this.request.status)
    },
    statusHistory() {
      return this.request.statusHistory || []
    },
    historyFields() {
      return [
        { key: 'oldStatus', label: this.$t('partner.order-request.history.old-status') },
        { key: 'newStatus', label: this.$t('partner.order-request.history.new-status') },
        { key: 'reason', label: this.$t('partner.order-request.history.reason') },
        { key: 'changedByUser', label: this.$t('partner.order-request.history.changed-by-user') },
        { key: 'createdAt', label: this.$t('partner.order-request.history.changed-at') },
      ]
    },
    decisionReason() {
      if (this.request.rejectionReason) {
        return this.request.rejectionReason
      }

      if (this.request.changesRequestReason) {
        return this.request.changesRequestReason
      }

      return null
    },
    editDraftStorageKey() {
      return getEditDraftStorageKey(this.$route.params.id)
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (!this.isEditable || this.loading) {
          return
        }

        saveOrderRequestDraft(this.editDraftStorageKey, this.form)
      },
    },
    '$route.params.id': {
      handler() {
        this.initializePage()
      },
    },
  },
  created() {
    this.initializePage()
  },
  mounted() {
    this.subscribeToPusher()
  },
  beforeDestroy() {
    this.unsubscribeFromPusher()
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId)
      this.refreshTimeoutId = null
    }
  },
  methods: {
    async initializePage() {
      this.loading = true
      this.apiError = null
      this.restoredDraftAt = null
      this.partnerOrderRequestInstruction = null

      try {
        const [allowedCustomersResponse] = await Promise.all([
          this.$store.dispatch('externalOrderRequest/fetchAllowedCustomers'),
          this.$store.dispatch('externalOrderRequest/fetchPartnerOrderRequestById', this.$route.params.id),
        ])

        this.partnerOrderRequestInstruction = allowedCustomersResponse?.data?.orderRequestInstruction || null

        this.form = mapOrderRequestToForm(this.request)
        this.restoreDraftIfExists()
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner.order-request.errors.fetch-details')
      } finally {
        this.loading = false
      }
    },
    scheduleRealtimeRefresh() {
      if (this.refreshTimeoutId) {
        return
      }

      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null
        this.initializePage()
      }, 350)
    },
    subscribeToPusher() {
      if (this.pusherExternalOrderRequestHandler) {
        return
      }

      this.pusherExternalOrderRequestHandler = payload => {
        const payloadId = payload?.id
        if (payloadId !== undefined && payloadId !== null
          && String(payloadId) !== String(this.$route.params.id)) {
          return
        }

        this.scheduleRealtimeRefresh()
      }

      PusherClient.bind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
    },
    unsubscribeFromPusher() {
      if (!this.pusherExternalOrderRequestHandler) {
        return
      }

      PusherClient.unbind('update', 'external_order_request', this.pusherExternalOrderRequestHandler, this)
      this.pusherExternalOrderRequestHandler = null
    },
    restoreDraftIfExists() {
      const draft = readOrderRequestDraft(this.editDraftStorageKey)
      if (!draft?.payload || !this.isEditable) {
        return
      }

      this.form = {
        ...this.form,
        ...draft.payload,
      }
      this.restoredDraftAt = draft.savedAt || null
    },
    clearAutosave() {
      clearOrderRequestDraft(this.editDraftStorageKey)
      this.form = mapOrderRequestToForm(this.request)
      this.restoredDraftAt = null
      window.toast.notify.success(this.$t('partner.order-request.autosave.cleared'))
    },
    async persistDraft(showSuccessToast = true) {
      const payload = buildOrderRequestPayloadFromForm(this.form)
      const missingFields = validateOrderRequestPayload(payload)
      if (missingFields.length > 0) {
        this.apiError = this.$t('partner.order-request.errors.required-fields')
        throw new Error('validation')
      }

      const response = await this.$store.dispatch('externalOrderRequest/updatePartnerOrderRequest', {
        id: this.$route.params.id,
        data: payload,
      })
      this.form = mapOrderRequestToForm(response.data)
      clearOrderRequestDraft(this.editDraftStorageKey)
      this.restoredDraftAt = null

      if (showSuccessToast) {
        window.toast.notify.success(this.$t('partner.order-request.notifications.saved'))
      }

      return response
    },
    async saveDraft() {
      if (this.saving || !this.isEditable) {
        return
      }

      this.saving = true
      this.apiError = null

      try {
        await this.persistDraft(true)
      } catch (error) {
        if (error?.message !== 'validation') {
          parseRequestError(error)
          this.apiError = error?.response?.data?.message || this.$t('partner.order-request.errors.update')
        }
      } finally {
        this.saving = false
      }
    },
    async submitRequest() {
      if (this.submitting || !this.canSubmit) {
        return
      }

      this.submitting = true
      this.apiError = null

      try {
        if (this.isEditable) {
          await this.persistDraft(false)
        }

        const response = await this.$store.dispatch('externalOrderRequest/submitPartnerOrderRequest', this.$route.params.id)
        this.form = mapOrderRequestToForm(response.data)
        clearOrderRequestDraft(this.editDraftStorageKey)
        this.restoredDraftAt = null
        window.toast.notify.success(this.$t('partner.order-request.notifications.submitted'))
      } catch (error) {
        if (error?.message !== 'validation') {
          parseRequestError(error)
          this.apiError = error?.response?.data?.message || this.$t('partner.order-request.errors.submit')
        }
      } finally {
        this.submitting = false
      }
    },
    async cancelRequest() {
      if (this.cancelling || !this.canCancel) {
        return
      }

      this.cancelling = true
      this.apiError = null

      try {
        const response = await this.$store.dispatch('externalOrderRequest/cancelPartnerOrderRequest', this.$route.params.id)
        this.form = mapOrderRequestToForm(response.data)
        clearOrderRequestDraft(this.editDraftStorageKey)
        this.restoredDraftAt = null
        window.toast.notify.success(this.$t('partner.order-request.notifications.cancelled'))
      } catch (error) {
        parseRequestError(error)
        this.apiError = error?.response?.data?.message || this.$t('partner.order-request.errors.cancel')
      } finally {
        this.cancelling = false
      }
    },
    statusLabel(status) {
      if (!status) {
        return '-'
      }

      const i18nKey = `partner.order-request.statuses.${status}`
      if (typeof this.$te === 'function' && this.$te(i18nKey)) {
        return this.$t(i18nKey)
      }

      return status
    },
    statusVariant(status) {
      if (status === 'accepted') {
        return 'success'
      }
      if (status === 'rejected' || status === 'cancelled') {
        return 'danger'
      }
      if (status === 'changes_requested') {
        return 'warning'
      }
      return 'secondary'
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      return transformDatetimeToHumanReadablePretty(value)
    },
    changedByUserLabel(item) {
      const user = item?.changedByUser
      if (!user || typeof user !== 'object') {
        return '-'
      }

      if (typeof user.fullName === 'string' && user.fullName.trim() !== '') {
        return user.fullName
      }

      const chunks = []
      if (typeof user.name === 'string' && user.name.trim() !== '') {
        chunks.push(user.name.trim())
      }
      if (typeof user.surname === 'string' && user.surname.trim() !== '') {
        chunks.push(user.surname.trim())
      }

      return chunks.length > 0 ? chunks.join(' ') : '-'
    },
  },
}
</script>

<style lang="scss" scoped>
.gap-1 {
  gap: 0.5rem;
}
</style>
